import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Search, RotateCcw, SlidersHorizontal, Eye, ChevronsUpDown, Check } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function FiltersBar({ filters, setFilters, suppliers, buffer, onBufferChange, showMode, onShowModeChange, slowDays, onSlowDaysChange }) {
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);

  const handleReset = () => {
    setFilters({ supplier: 'all', dateFrom: null, dateTo: null, search: '' });
  };

  const hasFilters = filters.supplier !== 'all' || filters.dateFrom || filters.dateTo || filters.search;

  const modeButtons = [
    { value: 'all', label: 'All Products' },
    { value: 'sold_only', label: 'Sold Only' },
    { value: 'dead', label: 'Dead Stock' },
    { value: 'inactive', label: 'Show Inactive' }
  ];

  const selectedSupplierLabel = filters.supplier === 'all' ? 'All Suppliers' : filters.supplier;

  return (
    <div className="border border-zinc-200 bg-white p-4 space-y-3">
      {/* Header row with view mode toggles */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-zinc-400" />
          <span className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Filters</span>
        </div>

        <div className="flex items-center gap-1" data-testid="view-mode-toggles">
          <Eye className="h-3.5 w-3.5 text-zinc-400 mr-1" />
          {modeButtons.map(m => (
            <button
              key={m.value}
              data-testid={`view-mode-${m.value}`}
              onClick={() => onShowModeChange(m.value)}
              className={`px-3 py-1 text-xs font-medium transition-colors border ${
                showMode === m.value
                  ? 'bg-zinc-900 text-white border-zinc-900'
                  : 'bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
        {/* Supplier - Searchable Combobox */}
        <div>
          <label className="field-label">Supplier</label>
          <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="filter-supplier-select"
                variant="outline"
                role="combobox"
                aria-expanded={supplierOpen}
                className="w-full h-9 rounded-none border-zinc-300 justify-between text-left font-normal text-sm hover:bg-zinc-50"
              >
                <span className="truncate">{selectedSupplierLabel}</span>
                <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0 rounded-none" align="start">
              <Command>
                <CommandInput placeholder="Type supplier name..." className="h-9 text-sm" />
                <CommandList>
                  <CommandEmpty className="py-3 text-xs text-zinc-400">No supplier found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => { setFilters(prev => ({ ...prev, supplier: 'all' })); setSupplierOpen(false); }}
                      className="rounded-none text-sm cursor-pointer"
                    >
                      <Check className={cn("mr-2 h-3.5 w-3.5", filters.supplier === 'all' ? "opacity-100" : "opacity-0")} />
                      All Suppliers
                    </CommandItem>
                    {suppliers.map(s => (
                      <CommandItem
                        key={s}
                        value={s}
                        onSelect={() => { setFilters(prev => ({ ...prev, supplier: s })); setSupplierOpen(false); }}
                        className="rounded-none text-sm cursor-pointer"
                      >
                        <Check className={cn("mr-2 h-3.5 w-3.5", filters.supplier === s ? "opacity-100" : "opacity-0")} />
                        {s}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Date From */}
        <div>
          <label className="field-label">From</label>
          <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="filter-date-from"
                variant="outline"
                className="w-full h-9 rounded-none border-zinc-300 justify-start text-left font-normal text-sm hover:bg-zinc-50"
              >
                <CalendarIcon className="mr-2 h-3.5 w-3.5 text-zinc-400" />
                {filters.dateFrom ? format(filters.dateFrom, 'dd-MM-yyyy') : <span className="text-zinc-400">Start date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none" align="start">
              <Calendar
                mode="single"
                selected={filters.dateFrom}
                onSelect={(d) => { setFilters(prev => ({ ...prev, dateFrom: d })); setDateFromOpen(false); }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Date To */}
        <div>
          <label className="field-label">To</label>
          <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="filter-date-to"
                variant="outline"
                className="w-full h-9 rounded-none border-zinc-300 justify-start text-left font-normal text-sm hover:bg-zinc-50"
              >
                <CalendarIcon className="mr-2 h-3.5 w-3.5 text-zinc-400" />
                {filters.dateTo ? format(filters.dateTo, 'dd-MM-yyyy') : <span className="text-zinc-400">End date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-none" align="start">
              <Calendar
                mode="single"
                selected={filters.dateTo}
                onSelect={(d) => { setFilters(prev => ({ ...prev, dateTo: d })); setDateToOpen(false); }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Search */}
        <div>
          <label className="field-label">Search</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
            <Input
              data-testid="filter-search-input"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Product or barcode..."
              className="pl-8 rounded-none border-zinc-300 h-9 focus:border-emerald-600 focus-visible:ring-0"
            />
          </div>
        </div>

        {/* Buffer */}
        <div>
          <label className="field-label">Buffer %</label>
          <div className="relative">
            <Input
              data-testid="buffer-input"
              type="number"
              min="0"
              max="100"
              value={buffer}
              onChange={(e) => onBufferChange(Number(e.target.value))}
              className="rounded-none border-zinc-300 h-9 pr-8 font-mono text-sm focus:border-emerald-600 focus-visible:ring-0"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 font-mono">%</span>
          </div>
        </div>

        {/* Slow product threshold */}
        <div>
          <label className="field-label" title="Products with no sales for this many days get the Slow/Dead badge (applies everywhere)">Slow After</label>
          <Select value={String(slowDays || 30)} onValueChange={(v) => onSlowDaysChange?.(Number(v))}>
            <SelectTrigger data-testid="slow-days-select" className="rounded-none border-zinc-300 h-9 w-[110px] text-sm font-mono">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="30" className="rounded-none text-sm">30 days</SelectItem>
              <SelectItem value="45" className="rounded-none text-sm">45 days</SelectItem>
              <SelectItem value="60" className="rounded-none text-sm">60 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reset */}
        <div className="flex items-end">
          {hasFilters && (
            <Button
              data-testid="filter-reset-button"
              variant="ghost"
              onClick={handleReset}
              className="rounded-none text-zinc-500 hover:text-zinc-900 h-9 px-3"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
